<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from "vue";
import { EllipsisVerticalIcon, TrashIcon } from "@heroicons/vue/24/outline";

/**
 * Row-level kebab menu used by HomeView. Future wishlist items
 * (Rename, Download, Share) plug in as additional menu entries —
 * the design doc's "Future-feature fit" table commits to this slot.
 *
 * V0 has a single item (Delete job). The menu closes on:
 *   - selecting an item
 *   - clicking outside
 *   - ESC
 *
 * The kebab button itself emits no events — only the menu items do.
 * That keeps parent components from having to track an open/closed
 * state per row; the row only cares about which action fired.
 */
const props = defineProps<{
  /** For data-testid scoping so multiple rows can be targeted. */
  jobId: string;
}>();

// The emit carries `jobId` so the menu is self-describing: a caller
// that reuses the component outside HomeView (per the design doc's
// Future-feature fit table — Rename, Download, Share, Retry all plug
// in here) can't accidentally misroute the action to the wrong row.
// Caught by type-design-analyzer in the PR #810 review pass.
const emit = defineEmits<{ delete: [jobId: string] }>();

const open = ref(false);
const rootEl = ref<HTMLDivElement | null>(null);

function toggle(event: MouseEvent) {
  // Stop propagation so a parent row's click handler (which navigates
  // to the detail view on HomeView) doesn't fire when the user is
  // just opening the menu.
  event.stopPropagation();
  open.value = !open.value;
}

function close() {
  open.value = false;
}

function onDocClick(event: MouseEvent) {
  if (!open.value) return;
  const target = event.target as Node | null;
  if (target && rootEl.value && !rootEl.value.contains(target)) {
    open.value = false;
  }
}

function onKeyDown(event: KeyboardEvent) {
  if (open.value && event.key === "Escape") {
    open.value = false;
  }
}

onMounted(() => {
  document.addEventListener("click", onDocClick, true);
  document.addEventListener("keydown", onKeyDown);
});
onBeforeUnmount(() => {
  document.removeEventListener("click", onDocClick, true);
  document.removeEventListener("keydown", onKeyDown);
});

function pickDelete(event: MouseEvent) {
  event.stopPropagation();
  open.value = false;
  emit("delete", props.jobId);
}
</script>

<template>
  <div ref="rootEl" class="relative inline-block">
    <button
      type="button"
      :data-testid="`job-row-kebab-${jobId}`"
      :aria-haspopup="true"
      :aria-expanded="open"
      :aria-label="`Open actions for job ${jobId}`"
      class="rounded p-1 text-text-tertiary hover:bg-bg-hover hover:text-text-secondary transition-colors"
      @click="toggle"
    >
      <EllipsisVerticalIcon class="h-4 w-4" aria-hidden="true" />
    </button>
    <Transition
      enter-active-class="transition ease-out duration-100"
      enter-from-class="opacity-0 scale-95"
      enter-to-class="opacity-100 scale-100"
      leave-active-class="transition ease-in duration-75"
      leave-from-class="opacity-100 scale-100"
      leave-to-class="opacity-0 scale-95"
    >
      <div
        v-if="open"
        :data-testid="`job-row-menu-${jobId}`"
        role="menu"
        class="absolute right-0 top-full mt-1 w-40 rounded-md border border-border bg-bg-main shadow-lg z-30 py-1"
      >
        <button
          type="button"
          role="menuitem"
          :data-testid="`job-row-menu-delete-${jobId}`"
          class="w-full text-left px-3 py-2 text-sm text-error hover:bg-bg-hover inline-flex items-center gap-2 transition-colors"
          @click="pickDelete"
        >
          <TrashIcon class="h-4 w-4" aria-hidden="true" />
          Delete job
        </button>
      </div>
    </Transition>
  </div>
</template>
